import { Mic, Phone } from 'lucide-react';
import { Fragment, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import { useCall } from '@apps/web/context/call_provider';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import {
    useOrganization,
    useOrganizationCaregiver,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

// Scoped per user id so a shared workstation never prefills one
// coordinator's callback number for the next user.
function coordinatorPhoneStorageKey(userId: string): string {
    return `phoebe.click-to-call.coordinator-phone.${userId}`;
}

function getStoredCoordinatorPhone(userId: string | undefined): string | null {
    if (!userId) {
        return null;
    }
    try {
        return localStorage.getItem(coordinatorPhoneStorageKey(userId));
    } catch {
        return null;
    }
}

function storeCoordinatorPhone(
    userId: string | undefined,
    phoneNumber: string,
) {
    if (!userId) {
        return;
    }
    try {
        localStorage.setItem(coordinatorPhoneStorageKey(userId), phoneNumber);
    } catch {
        // Remembering the number is best-effort only.
    }
}

type ClickToCallDialogProps = {
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly caregiverName: string | null;
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
};

export function ClickToCallDialog({
    organizationId,
    caregiverId,
    caregiverName,
    open,
    onOpenChange,
}: ClickToCallDialogProps) {
    const { data: userProfile } = useUserProfile({
        reason: 'click-to-call-dialog',
    });
    const { data: organization } = useOrganization(organizationId, {
        reason: 'click-to-call-dialog',
    });
    const browserCallingEnabled = useFeatureFlag(
        'click_to_call_browser_enabled',
        organization?.features,
    );
    const { startCall, status: browserCallStatus } = useCall();
    const { data: caregiver, isLoading: isLoadingCaregiver } =
        useOrganizationCaregiver(organizationId, open ? caregiverId : null, {
            reason: 'click-to-call-dialog',
        });
    const [enteredPhoneNumber, setEnteredPhoneNumber] = useState<string | null>(
        null,
    );
    const [isCalling, setIsCalling] = useState(false);

    const coordinatorPhoneNumber =
        enteredPhoneNumber ??
        getStoredCoordinatorPhone(userProfile?.user.id) ??
        userProfile?.user.phone_number ??
        '';
    const phoneContacts = (caregiver?.contacts ?? []).filter(
        (contact) => contact.type === 'phone',
    );
    const caregiverPhoneNumber =
        (
            phoneContacts.find((contact) => contact.is_primary === true) ??
            phoneContacts[0]
        )?.address ?? null;
    const displayName = caregiverName ?? caregiver?.name ?? 'caregiver';

    async function handleStartBridgeCall() {
        setIsCalling(true);
        try {
            storeCoordinatorPhone(userProfile?.user.id, coordinatorPhoneNumber);
            await getApiClient().post({
                url: '/organizations/{organization_id}/click-to-call/calls',
                pathParams: { organization_id: organizationId },
                body: {
                    caregiver_id: caregiverId,
                    coordinator_phone_number: coordinatorPhoneNumber,
                    call_mode: 'bridge',
                },
            });
            toast.success('Calling your phone now', {
                description: `Answer to be connected to ${displayName}.`,
                persistNotification: false,
            });
            onOpenChange(false);
        } catch (error) {
            console.error('Error starting click-to-call:', error);
            toast.error('Failed to start the call', {
                description: 'Please try again.',
                persistNotification: false,
            });
        } finally {
            setIsCalling(false);
        }
    }

    async function handleStartBrowserCall() {
        // The floating in-call panel (rendered by CallProvider) takes over
        // from here; close the dialog immediately.
        onOpenChange(false);
        await startCall({
            organizationId,
            caregiverId,
            caregiverName: displayName,
        });
    }

    const startDisabled =
        isCalling ||
        !caregiverPhoneNumber ||
        (browserCallingEnabled
            ? browserCallStatus !== 'idle'
            : !coordinatorPhoneNumber.trim());

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Call {displayName}</DialogTitle>
                    <DialogDescription>
                        {browserCallingEnabled ? (
                            <span>
                                Calls {displayName}
                                {caregiverPhoneNumber
                                    ? ` at ${caregiverPhoneNumber}`
                                    : ''}{' '}
                                right from this window. They see your
                                organization’s number.
                            </span>
                        ) : (
                            <span>
                                Phoebe calls your phone first from your
                                organization’s number. When you answer, we
                                connect you to {displayName}
                                {caregiverPhoneNumber
                                    ? ` at ${caregiverPhoneNumber}`
                                    : ''}
                                .
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>
                {!isLoadingCaregiver && caregiver && !caregiverPhoneNumber ? (
                    <p className="text-destructive text-sm">
                        This caregiver has no phone number on file.
                    </p>
                ) : null}
                {browserCallingEnabled ? (
                    <div className="bg-muted/50 text-muted-foreground flex items-center gap-2 rounded-md px-3 py-2 text-sm">
                        <Mic className="h-4 w-4 shrink-0" />
                        Uses your computer’s microphone and speakers. The call
                        is recorded and transcribed.
                    </div>
                ) : null}
                {browserCallingEnabled ? null : (
                    <div className="space-y-2">
                        <Label htmlFor="click-to-call-coordinator-phone">
                            Your phone number
                        </Label>
                        <Input
                            id="click-to-call-coordinator-phone"
                            placeholder="(555) 555-5555"
                            type="tel"
                            value={coordinatorPhoneNumber}
                            onChange={(event) =>
                                setEnteredPhoneNumber(event.target.value)
                            }
                            disabled={isCalling}
                        />
                    </div>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isCalling}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        onClick={() => {
                            if (browserCallingEnabled) {
                                void handleStartBrowserCall();
                            } else {
                                void handleStartBridgeCall();
                            }
                        }}
                        disabled={startDisabled}
                    >
                        <Phone className="mr-2 h-4 w-4" />
                        {isCalling ? 'Starting call…' : 'Start call'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

type ClickToCallButtonProps = {
    readonly organizationId: string;
    readonly caregiverId: string;
    readonly caregiverName: string | null;
    readonly iconOnly?: boolean;
};

// Renders nothing unless the organization has click_to_call_enabled, so
// callers can drop it into a toolbar without their own flag check.
export function ClickToCallButton({
    organizationId,
    caregiverId,
    caregiverName,
    iconOnly = false,
}: ClickToCallButtonProps) {
    const { data: organization } = useOrganization(organizationId, {
        reason: 'click-to-call-button',
    });
    const clickToCallEnabled = useFeatureFlag(
        'click_to_call_enabled',
        organization?.features,
    );
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const callLabel = caregiverName ? `Call ${caregiverName}` : 'Call';

    if (!clickToCallEnabled) {
        return null;
    }

    return (
        <Fragment>
            <Button
                aria-label={iconOnly ? callLabel : undefined}
                className={iconOnly ? 'h-11 w-11 md:h-8 md:w-8' : 'shrink-0'}
                onClick={() => setIsDialogOpen(true)}
                size={iconOnly ? 'icon' : 'sm'}
                type="button"
                variant={iconOnly ? 'ghost' : 'outline'}
            >
                <Phone className={iconOnly ? 'h-4 w-4' : 'mr-2 h-4 w-4'} />
                {iconOnly ? null : 'Call'}
            </Button>
            <ClickToCallDialog
                organizationId={organizationId}
                caregiverId={caregiverId}
                caregiverName={caregiverName}
                open={isDialogOpen}
                onOpenChange={setIsDialogOpen}
            />
        </Fragment>
    );
}
